//
// Copyright (c) 2021 Astute Global, Inc.
//

import {
  diff,
  _extractTextFromLinkRange,
  findAttributeRange,
  first,
  isLinkableElement,
  LinkCommand,
  ModelLivePosition,
  toMap,
  ensureSafeUrl,
} from "ckeditor5";
import { PLACEHOLDER_VALUE } from "../ckEditorConstants";
import {
  ATTRIBUTE_CONTENT_MAPPING,
  ATTRIBUTE_LINK_TOPIC,
  ATTRIBUTE_LINK_TYPE,
  LINK_TYPE,
} from "./util";

export default class AkLinkCommand extends LinkCommand {
  constructor(editor, linkCommand) {
    // noinspection JSCheckFunctionSignatures
    super(editor);
    this.set(ATTRIBUTE_CONTENT_MAPPING, undefined);
    this.set(ATTRIBUTE_LINK_TOPIC, undefined);
    this.set(ATTRIBUTE_LINK_TYPE, LINK_TYPE.URL);
    if (linkCommand) {
      this.manualDecorators = linkCommand.manualDecorators;
      this.automaticDecorators = linkCommand.automaticDecorators;
    }
  }

  execute(href, manualDecoratorIds = {}, displayedText) {
    const model = this.editor.model;
    const selection = model.document.selection;
    const linkType =
      manualDecoratorIds[ATTRIBUTE_LINK_TYPE] || this[ATTRIBUTE_LINK_TYPE] || LINK_TYPE.URL;
    const allowedProtocols = this.editor.config.get("link.allowedProtocols");

    model.change((writer) => {
      const updateLinkAttributes = (itemOrRange) => {
        writer.setAttribute("linkHref", ensureSafeUrl(href, allowedProtocols), itemOrRange);
        for (const [key, value] of Object.entries(manualDecoratorIds)) {
          if (value && value != PLACEHOLDER_VALUE) {
            writer.setAttribute(key, value, itemOrRange);
          } else {
            writer.removeAttribute(key, itemOrRange);
          }
        }
      };
      const updateLinkTextIfNeeded = (range, linkHref) => {
        const linkText = _extractTextFromLinkRange(range);
        if (!linkText) {
          return range;
        }
        // Make a copy not to override the command param value.
        let newText = displayedText;
        if (!newText) {
          // Replace the link text with the new href if previously href was equal to text.
          // For example: `<a href="http://ckeditor.com/">http://ckeditor.com/</a>`.
          newText = linkHref && linkHref == linkText ? href : linkText;
        }
        // Only if needed.
        if (newText != linkText) {
          const changes = findChanges(linkText, newText);
          let insertsLength = 0;
          for (const { offset, actual, expected } of changes) {
            const updatedOffset = offset + insertsLength;
            const subRange = writer.createRange(
              range.start.getShiftedBy(updatedOffset),
              range.start.getShiftedBy(updatedOffset + actual.length),
            );
            // Collect formatting attributes from replaced text.
            const textNode = getLinkPartTextNode(subRange, range);
            const attributes = textNode.getAttributes();
            const formattingAttributes = Array.from(attributes).filter(
              ([key]) => model.schema.getAttributeProperties(key).isFormatting,
            );
            // Create a new text node.
            const newTextNode = writer.createText(expected, formattingAttributes);
            // Set link attributes before inserting to document to avoid Differ attributes edge case.
            updateLinkAttributes(newTextNode);
            // Replace text with formatting.
            model.insertContent(newTextNode, subRange);
            // Sum of all previous inserts.
            insertsLength += expected.length;
          }
          return writer.createRange(range.start, range.start.getShiftedBy(newText.length));
        }
      };
      const collapseSelectionAtLinkEnd = (linkRange) => {
        const { plugins } = this.editor;
        writer.setSelection(linkRange.end);
        if (plugins.has("TwoStepCaretMovement")) {
          // After replacing the text of the link, we need to move the caret to the end of the link,
          // override it's gravity to forward to prevent keeping e.g. bold attribute on the caret
          // which was previously inside the link.
          //
          // If the plugin is not available, the caret will be placed at the end of the link and the
          // bold attribute will be kept even if command moved caret outside the link.
          plugins.get("TwoStepCaretMovement")._handleForwardMovement();
        } else {
          // Remove the `linkHref` attribute and all link decorators from the selection.
          // It stops adding a new content into the link element.
          for (const key of ["linkHref", ...Object.keys(manualDecoratorIds)]) {
            writer.removeSelectionAttribute(key);
          }
        }
      };
      // If selection is collapsed then update selected link or insert new one at the place of caret.
      if (selection.isCollapsed) {
        const position = selection.getFirstPosition();

        // When selection is inside text with the `linkHref` attribute.
        if (selection.hasAttribute("linkHref")) {
          const linkHref = selection.getAttribute("linkHref");
          const linkRange = findAttributeRange(position, "linkHref", linkHref, model);
          const newLinkRange = updateLinkTextIfNeeded(linkRange, linkHref);
          updateLinkAttributes(newLinkRange || linkRange);
          if (newLinkRange) {
            collapseSelectionAtLinkEnd(newLinkRange);
          }
        } else if (href) {
          // If not then insert text node with `linkHref` attribute in place of the caret.
          // However, since selection is collapsed, attribute value will be used as data for the
          // text node. So, if `href` is empty, do not create the text node.
          const attributes = toMap(selection.getAttributes());

          attributes.set("linkHref", ensureSafeUrl(href, allowedProtocols));
          for (const [key, value] of Object.entries(manualDecoratorIds)) {
            if (value && value != PLACEHOLDER_VALUE) {
              attributes.set(key, value);
            } else {
              attributes.delete(key);
            }
          }

          let defaultText;
          switch (linkType) {
            case LINK_TYPE.CONTENT_MAPPING:
              defaultText = JSON.parse(manualDecoratorIds[ATTRIBUTE_CONTENT_MAPPING]).name;
              break;
            case LINK_TYPE.TOPIC_LINK:
              defaultText = JSON.parse(manualDecoratorIds[ATTRIBUTE_LINK_TOPIC]).label;
              break;
            default:
              defaultText = href;
              break;
          }
          const newLinkRange = model.insertContent(
            writer.createText(displayedText || defaultText, attributes),
            position,
          );
          collapseSelectionAtLinkEnd(newLinkRange);
        }
      } else {
        // If selection has non-collapsed ranges, we change the attribute on nodes inside those ranges
        // omitting nodes where the `linkHref` attribute is disallowed.
        const selectionRanges = Array.from(selection.getRanges());
        const ranges = model.schema.getValidRanges(selectionRanges, "linkHref");

        // But for the first, check whether the `linkHref` attribute is allowed on selected blocks
        // (e.g., the "image" element).
        const allowedRanges = [];
        for (const element of selection.getSelectedBlocks()) {
          if (model.schema.checkAttribute(element, "linkHref")) {
            allowedRanges.push(writer.createRangeOn(element));
          }
        }

        // Ranges that accept the `linkHref` attribute. Since we will iterate over `allowedRanges`, let's clone it.
        const rangesToUpdate = allowedRanges.slice();

        // For all selection ranges we want to check whether the given range is inside an element that accepts the
        // `linkHref` attribute. If so, we don't want to propagate applying the attribute to its children.
        for (const range of ranges) {
          if (this._isRangeToUpdate(range, allowedRanges)) {
            rangesToUpdate.push(range);
          }
        }

        // Store the selection ranges in a pseudo live range array (stickiness to the outside of the range).
        const stickyPseudoRanges = selectionRanges.map((range) => ({
          start: ModelLivePosition.fromPosition(range.start, "toPrevious"),
          end: ModelLivePosition.fromPosition(range.end, "toNext"),
        }));

        // Update or set links (including text update if needed).
        for (let range of rangesToUpdate) {
          const linkHref = (range.start.textNode || range.start.nodeAfter).getAttribute("linkHref");
          range = updateLinkTextIfNeeded(range, linkHref) || range;
          updateLinkAttributes(range);
        }

        // The original selection got trimmed by replacing content so we need to restore it.
        writer.setSelection(
          stickyPseudoRanges.map((pseudoRange) => {
            const start = pseudoRange.start.toPosition();
            const end = pseudoRange.end.toPosition();
            pseudoRange.start.detach();
            pseudoRange.end.detach();
            return model.createRange(start, end);
          }),
        );
      }
    });
  }

  refresh() {
    super.refresh();
    const model = this.editor.model;
    const selection = model.document.selection;
    const selectedElement = selection.getSelectedElement() || first(selection.getSelectedBlocks());
    const extraAttributes = [ATTRIBUTE_CONTENT_MAPPING, ATTRIBUTE_LINK_TOPIC];
    if (isLinkableElement(selectedElement, model.schema)) {
      extraAttributes.forEach((attributeName) =>
        this.set(attributeName, selectedElement.getAttribute(attributeName)),
      );
    } else {
      extraAttributes.forEach((attributeName) =>
        this.set(attributeName, selection.getAttribute(attributeName)),
      );
    }
    this.set(ATTRIBUTE_LINK_TYPE, this._determineLinkType());
  }

  _determineLinkType() {
    const topicJson = this[ATTRIBUTE_LINK_TOPIC];
    if (topicJson && topicJson != PLACEHOLDER_VALUE) {
      return LINK_TYPE.TOPIC_LINK;
    }
    const contentMappingJson = this[ATTRIBUTE_CONTENT_MAPPING];
    if (contentMappingJson && contentMappingJson != PLACEHOLDER_VALUE) {
      return LINK_TYPE.CONTENT_MAPPING;
    }
    const href = this.value;
    if (!href) {
      return LINK_TYPE.URL;
    }
    if (href.startsWith("mailto:")) {
      return LINK_TYPE.EMAIL;
    }
    if (href.startsWith("tel:")) {
      return LINK_TYPE.PHONE;
    }
    return LINK_TYPE.URL;
  }
}
/**
 * Compares two strings and returns an array of changes needed to transform one into another.
 * Uses the diff utility to find the differences and groups them into chunks containing information
 * about the offset and actual/expected content.
 *
 * @param oldText The original text to compare.
 * @param newText The new text to compare against.
 * @returns Array of change objects containing offset and actual/expected content.
 *
 * @example
 * findChanges( 'hello world', 'hi there' );
 *
 * Returns:
 * [
 * 	{
 * 		"offset": 1,
 * 		"actual": "ello",
 * 		"expected": "i"
 * 	},
 * 	{
 * 		"offset": 2,
 * 		"actual": "wo",
 * 		"expected": "the"
 * 	},
 * 	{
 * 		"offset": 3,
 * 		"actual": "ld",
 * 		"expected": "e"
 * 	}
 * ]
 */
function findChanges(oldText, newText) {
  // Get array of operations (insert/delete/equal) needed to transform oldText into newText.
  // Example: diff('abc', 'abxc') returns ['equal', 'equal', 'insert', 'equal']
  const changes = diff(oldText, newText);
  // Track position in both strings based on operation type.
  const counter = { equal: 0, insert: 0, delete: 0 };
  const result = [];
  // Accumulate consecutive changes into slices before creating change objects.
  let actualSlice = "";
  let expectedSlice = "";
  // Adding null as sentinel value to handle final accumulated changes.
  for (const action of [...changes, null]) {
    if (action == "insert") {
      // Example: for 'abc' -> 'abxc', at insert position, adds 'x' to expectedSlice.
      expectedSlice += newText[counter.equal + counter.insert];
    } else if (action == "delete") {
      // Example: for 'abc' -> 'ac', at delete position, adds 'b' to actualSlice.
      actualSlice += oldText[counter.equal + counter.delete];
    } else if (actualSlice.length || expectedSlice.length) {
      // On 'equal' or end: bundle accumulated changes into a single change object.
      // Example: { offset: 2, actual: "", expected: "x" }
      result.push({
        offset: counter.equal,
        actual: actualSlice,
        expected: expectedSlice,
      });
      actualSlice = "";
      expectedSlice = "";
    }
    // Increment appropriate counter for the current operation.
    if (action) {
      counter[action]++;
    }
  }
  return result;
}
/**
 * Returns text node withing the link range that should be updated.
 *
 * @param range Partial link range.
 * @param linkRange Range of the entire link.
 * @returns Text node.
 */
function getLinkPartTextNode(range, linkRange) {
  if (!range.isCollapsed) {
    return first(range.getItems());
  }
  const position = range.start;
  if (position.textNode) {
    return position.textNode;
  }
  // If the range is at the start of a link range then prefer node inside a link range.
  if (!position.nodeBefore || position.isEqual(linkRange.start)) {
    return position.nodeAfter;
  } else {
    return position.nodeBefore;
  }
}
